import React from 'react';

import { ImMNElement } from '../../../common/elements/elementModelTypes';
import { ELEMENT_DEFAULT_WIDTH } from '../../../common/elements/elementConstants';
import makeCanDropFn from '../dnd/canDrop/makeCanDropFn';
import onlyTasks from '../dnd/canDrop/onlyTaskPredicate';
import List from '../../list/List';
import EmptyTaskListPlaceholder from './EmptyTaskListPlaceholder';
import TaskListFeatureSuggestions from './TaskListFeatureSuggestions';
import TaskListTitle from './taskListTitle/TaskListTitle';

const taskListCanDrop = makeCanDropFn([onlyTasks]);

type LegacyComponentProps = Record<string, unknown>;

const LegacyList = List as React.ComponentType<LegacyComponentProps>;
const LegacyEmptyTaskListPlaceholder = EmptyTaskListPlaceholder as React.ComponentType<LegacyComponentProps>;
const LegacyTaskListFeatureSuggestions = TaskListFeatureSuggestions as React.ComponentType<LegacyComponentProps>;
const LegacyTaskListTitle = TaskListTitle as React.ComponentType<LegacyComponentProps>;

export type LegacyTaskListEditorProps = Record<string, unknown> & {
    childElementIds?: string[];
    className?: string;
    currentBoardId?: string;
    element: ImMNElement;
    elementEvents?: {
        onMouseDown?: React.MouseEventHandler;
        onContextMenu?: React.MouseEventHandler;
    };
    elementId: string;
    gridSize?: number;
    inList?: string;
    isEditable: boolean;
    isEditing?: boolean;
    isFocusedForegroundElement?: boolean;
    isPresentationModeEnabled?: boolean;
    isPresentational?: boolean;
    isSelected?: boolean;
    isSingleSelected: boolean;
    permissions?: number;
    spellCheck?: boolean;
    currentEditorId?: string;
    showTitle: boolean;
    startEditing?: (args: { editorId: string; editorKey: string }) => void;
    setParentHoveredChildAcceptsDrop?: (acceptsDrop: boolean) => void;
    // These two are only forwarded on to the untyped legacy children, never called here, so they
    // match their one typed producer. A `(...args: unknown[])` catch-all is the opposite of
    // permissive - no specific signature can satisfy it - which is what broke the switch's spread.
    moveToTrashHandler?: (args: { elementId: string }) => unknown;
    changeIndentationHandler?: (...args: unknown[]) => unknown;
    goToPreviousTaskHandler?: (...args: unknown[]) => unknown;
    goToNextTaskHandler?: (...args: unknown[]) => unknown;
    onStartBackspaceHandler?: (...args: unknown[]) => unknown;
    onEndDeleteHandler?: (...args: unknown[]) => unknown;
    onTaskListUpdatedHandler?: (...args: unknown[]) => unknown;
    onTaskToggle?: (...args: unknown[]) => unknown;
    dispatchHideTitle?: (...args: unknown[]) => unknown;
    dispatchUpdateTitle?: (...args: unknown[]) => unknown;
    dispatchUpdateElement?: (...args: unknown[]) => unknown;
    getContextZoomScale?: (...args: unknown[]) => unknown;
    getContextZoomTranslationPx?: (...args: unknown[]) => unknown;
    selectThisElement?: (...args: unknown[]) => unknown;
};

/**
 * Renders the legacy task-list title, nested task list, empty placeholder and feature suggestions.
 */
const LegacyTaskListEditor: React.FC<LegacyTaskListEditorProps> = (props) => {
    const {
        childElementIds = [],
        element,
        elementEvents,
        elementId,
        isEditable,
        isPresentationModeEnabled,
        isSelected,
        isSingleSelected,
        showTitle,
        setParentHoveredChildAcceptsDrop,
        moveToTrashHandler,
        changeIndentationHandler,
        goToPreviousTaskHandler,
        goToNextTaskHandler,
        onStartBackspaceHandler,
        onEndDeleteHandler,
        onTaskListUpdatedHandler,
        onTaskToggle,
        dispatchUpdateElement,
        selectThisElement,
    } = props;

    const allowTaskDrag = showTitle || childElementIds.length > 1;
    const emptyTaskListComponent = !childElementIds.length ? (
        <LegacyEmptyTaskListPlaceholder largeTop={!showTitle} {...props} />
    ) : null;

    return (
        <>
            {showTitle && <LegacyTaskListTitle {...props} />}
            <LegacyList
                {...props}
                depth={0}
                enableDoubleClick={false}
                listId={elementId}
                setParentHoveredChildAcceptsDrop={setParentHoveredChildAcceptsDrop}
                listElementProps={{
                    isParentTaskListSelected: isSelected,
                    isParentTaskListSingleSelected: isSingleSelected,
                    taskListElementId: elementId,
                    allowDrag: allowTaskDrag,
                    noTitle: !showTitle,
                    moveToTrashHandler,
                    changeIndentationHandler,
                    goToPreviousTaskHandler,
                    goToNextTaskHandler,
                    onStartBackspaceHandler,
                    onEndDeleteHandler,
                    onTaskListUpdatedHandler,
                    depth: 0,
                    taskListOnMouseDown: elementEvents && elementEvents.onMouseDown,
                    taskListOnContextMenu: elementEvents && elementEvents.onContextMenu,
                    onTaskToggle,
                    selectParentTaskList: selectThisElement,
                }}
                placeholderComponent={emptyTaskListComponent}
                listCanDropFn={taskListCanDrop}
            />
            {!isPresentationModeEnabled && (
                <LegacyTaskListFeatureSuggestions
                    fullWidth
                    isEditable={isEditable}
                    element={element}
                    cardWidth={ELEMENT_DEFAULT_WIDTH}
                    dispatchUpdateElement={dispatchUpdateElement}
                />
            )}
        </>
    );
};

export default LegacyTaskListEditor;
